import { AfterViewInit, Component, ElementRef, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastService } from '../Services/toast.service';
import { ApiService } from '../Services/api.service';
import { AuthService } from '../Services/auth.service';
import { FormControl, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../material';
import { ActivatedRoute, Router } from '@angular/router';
import CryptoJS from 'crypto-js';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { RadioButtonModule } from 'primeng/radiobutton';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-search-id',
  standalone: true,
  imports: [ButtonModule, InputTextModule, TranslateModule, FormsModule, CommonModule, MaterialModule, TableModule, DialogModule, RadioButtonModule],
  providers: [ToastService, ApiService, AuthService, TranslateService],
  templateUrl: './search-id.component.html',
  styleUrl: './search-id.component.css'
})
export class SearchIdComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('searchInput') searchInput!: ElementRef;
  assuredID = new FormControl('');
  data: any;
  loading = false;
  pId: string;
  username: string;
  noDataAvailable: boolean = false;
  permissions: any;
  assuredNumber: any;
  userID: any;
  medicalService = [];
  sidebarMenu: any;
  showFindLimitsButton: boolean = false;
  showInsertServices: boolean = false;
  showInsertChronicMedication: boolean = false;
  canInsertClaim: any;
  switchtolimit: boolean = false;
  key = CryptoJS.enc.Utf8.parse("PR0V1D3R!@#p0rt4");
  iv = CryptoJS.enc.Utf8.parse("1234567890123456");
  enableInsert: any;
  otpDigits: string[] = ['', '', '', '', '', ''];
  otpTimer: number = 0;
  otpTimerInterval: any;
  phoneNumber: string = '';
  completePhoneNumber: string = '';
  otpRequestCount: number = 0;
  maxOTPRequests: number = 2;
  showOTPSection: boolean = false;
  remainingAttempts: any;
  originalPhoneNumber: string = '';
  showSkipModal: boolean = false;
  otpId: string | null = null;
  printTemplate = environment.printTemplate
  skipReasons: any[] = [
    { labelKey: 'skipReasons.patient_not_present', value: 'Patient not present at facility' },
    { labelKey: 'skipReasons.no_phone', value: 'Patient does not have a phone' },
    { labelKey: 'skipReasons.phone_off', value: 'Patient phone is switched off' },
    { labelKey: 'skipReasons.network_issue', value: 'Network connectivity issue' },
    { labelKey: 'skipReasons.medical_emergency', value: 'Medical emergency - urgent service needed' },
    { labelKey: 'skipReasons.verify_later', value: 'Patient will verify later' },
    { labelKey: 'skipReasons.other', value: 'other' }
  ];
  selectedSkipReason: string = '';
  customReason: string = '';

  verificationSkipped: boolean = false;

  get isOtherSelected(): boolean {
    return this.selectedSkipReason === 'other';
  }

  // Identity the skip is remembered against — the same one the OTP endpoints use.
  get currentAssuredKey(): string {
    return String(this.data?.assuredID ?? this.assuredID?.value ?? this.assuredNumber ?? '');
  }

  get isInsertClaimDisabled(): boolean {
    if (!this.canInsertClaim) {
      return true;
    }
    if (this.verificationSkipped) {
      return false;
    }
    const isVerified = this.data?.isVerified;
    return isVerified === false || isVerified === null || isVerified === '';
  }


  get isSendOTPDisabled(): boolean {
    const isNumberVerified = this.data?.isVerified === true;
    const isNumberChanged = this.phoneNumber !== this.originalPhoneNumber;
    const isNumberValid = this.phoneNumber && this.phoneNumber.length >= 9;
    const isTimerActive = this.otpTimer > 0;
    const isMaxRequestsReached = this.otpRequestCount >= this.maxOTPRequests;

    if (isNumberVerified && !isNumberChanged) {
      return true;
    }

    return !isNumberValid || isTimerActive || isMaxRequestsReached;
  }
  constructor(
    private apiService: ApiService<any>,
    private authService: AuthService,
    private toastService: ToastService,
    private router: Router,
    private translateService: TranslateService,
    private route: ActivatedRoute

  ) { }

  ngOnInit(): void {
    this.assuredNumber = this.route.snapshot.paramMap.get('assuredID');
    if (this.assuredNumber) {
      this.assuredID.setValue(this.assuredNumber);
      this.fetchAssuredData();
    }
  }

  ngAfterViewInit() {
    this.searchInput.nativeElement.focus();
  }

  fetchAssuredData() {
    const assuredValue = this.assuredID?.value || this.assuredNumber;
    if (!assuredValue) {
      this.translateService.get('toast.pleaseenterassuredid').subscribe(text => this.toastService.showWarn(text));
      return;
    }

    this.loading = true;
    const providerId = this.authService.getCurrentUserPID();
    const endpoint = `api/AssuredPerson/GetAssuredPerson?providerId=${providerId}&cardNumber=${assuredValue}`;

    this.apiService.getAll(endpoint).subscribe({
      next: (response: any) => {
        if (!response.isRequestSuccessful || response.successResponse === null) {
          this.loading = false;
          this.translateService.get('toast.nodataavailable').subscribe(text => {
            this.toastService.showInfo(text);
          });
          this.assuredID.setValue('');
          this.noDataAvailable = true;
          return;
        }
        this.data = response.successResponse;
        // A skip stays in force for this assuredID until logout / token expiry.
        this.verificationSkipped = this.authService.isVerificationSkipped(this.currentAssuredKey);
        if(this.data){
          this.showOTPSection =  false;
        } else{
          this.showOTPSection = true;
        }
        this.getAssuredIdBalance();
        this.loading = false;

        if (this.data?.contactNo) {
          this.phoneNumber = this.data.contactNo.replace(/^\+?218/, '');
          this.originalPhoneNumber = this.phoneNumber;
        } else if (this.data?.contactNo === null) {
          this.phoneNumber = '';
          // Verification was skipped for this member, so a missing number is no longer blocking.
          if (!this.verificationSkipped) {
            this.translateService.get('toast.cannotinsertclaimbecausecontactnumberisnotadded').subscribe(text => {
              this.toastService.showError(text);
            });
          }
        }

        this.canInsertClaim = this.data?.employmentStatus === "Active" && this.data?.policyStatus === "Active";

        if (!this.canInsertClaim) {
          this.translateService.get('toast.cannotinsertclaim').subscribe(text => {
            this.toastService.showWarn(text);
          });
        }

        this.noDataAvailable = false;
        const searchid = 'searchid';
        this.getActionButtonsPermission(searchid);
      },
      error: (error) => {
        this.loading = false;
        this.toastService.showError(error);
      },
    });
  }

  sendOTP() {
    if (this.otpRequestCount >= this.maxOTPRequests) {
      this.translateService.get('toast.maxotprequestsreached').subscribe(text => {
        this.toastService.showWarn(text || 'Maximum OTP requests reached. Please try again later.');
      });
      return;
    }

    if (this.otpTimer > 0) {
      this.translateService.get('toast.otpcooldownactive').subscribe(text => {
        this.toastService.showWarn(text || `Please wait ${this.otpTimer} seconds before requesting another OTP.`);
      });
      return;
    }

    if (this.phoneNumber && this.phoneNumber.length >= 9) {
      this.loading = true;
      this.completePhoneNumber = '218' + this.phoneNumber;
      const data = {
        assuredID: this.data.assuredID,
        phoneNumber: this.completePhoneNumber
      };

      const endpoint = `api/OTP/SendOTP`;

      this.apiService.create(endpoint, data).subscribe({
        next: (response: any) => {
          this.loading = false;
          if (response.isRequestSuccessful === true) {
            this.otpRequestCount++;
            this.showOTPSection = true;
            this.remainingAttempts = response.successResponse?.remainingAttempts;
            const cooldownSeconds = response.successResponse?.cooldownSeconds;
            this.otpId = response.successResponse?.otpId ?? null;
            this.startOTPTimer(cooldownSeconds);
            this.translateService.get('toast.otpresent').subscribe(text => {
              this.toastService.showSuccess(text || 'OTP sent successfully!');
            });
          } else {
            this.toastService.showError(response.errorDetail.errorReason);
          }
        },
        error: (error: any) => {
          this.loading = false;
          this.toastService.showError(error.errorDetail.errorReason);
        }
      });
    } else {
      this.translateService.get('toast.pleaseenterphonenumber').subscribe(text => {
        this.toastService.showWarn(text || 'Please enter a valid phone number');
      });
    }
  }



  createInsertClaim(typeOfClaim: number) {
    this.loading = true
    this.pId = this.authService.getCurrentUserPID();
    this.username = this.authService.getUserName();
    let data = {
      assuredID: this.assuredID.value,
      providerID: Number(this.pId),
      providersUserName: this.username,
      typeOfClaim: typeOfClaim
    };
    this.apiService.create(`api/Claim/InsertClaim`, data).subscribe({
      next: (response: any) => {
        try {
          this.loading = false;
          if (typeOfClaim === 1) {
            this.router.navigate(['/portal/insert-claim/create-medication', response.successResponse.claimNumber.trim()])
          this.translateService.get('toast.claimcreatedsuccessfully').subscribe(text => this.toastService.showSuccess(text));
          } else if (typeOfClaim === 2) {
            this.router.navigate(['/portal/insert-claim/create-chronic-medication', response.successResponse.claimNumber.trim()])
          this.translateService.get('toast.claimcreatedsuccessfully').subscribe(text => this.toastService.showSuccess(text));
          }
        } catch (err) {
          this.loading = false;
          this.toastService.showError(err)
        }

      },
      error: (error: any) => {
        this.loading = false;
        this.toastService.showError(error)
      }
    });
  }

  decryptData(encryptedText: string) {
    try {
      const encryptedBytes = CryptoJS.enc.Base64.parse(encryptedText);
      const decrypted = CryptoJS.AES.decrypt(
        encryptedText,
        this.key,
        { iv: this.iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
      );
      const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);
      if (!decryptedText) throw new Error("Decryption resulted in empty data");
      return JSON.parse(decryptedText);
    } catch (error) {
      return null;
    }
  }

  getAssuredIdBalance() {
    this.apiService.getAll(`api/Claim/GetAssuredIdBalance?assuredId=${this.data.assuredID}`).subscribe({
      next: (response: any) => {
        let data = this.decryptData(response.data);
        this.enableInsert = data.SuccessResponse[0]
      },
      error: (error) => {
        this.toastService.showError(error)
      }
    }
    )
  }

  gotoNewCase(data: any) {
    this.router.navigate(['/portal/preapproval/createnewcase'], { state: { caseData: data } });
  }


  gotoServices() {
    this.router.navigate(['/portal/insert-claim'])
  }

  findLimits() {
    this.fetchMedicalServices()
  }


  private hasFindLimitsPermission(buttonPermissions: any[]): boolean {
    return buttonPermissions.some(button => button.name === 'findlimits');
  }

  private hasInsertServicesPermission(buttonPermissions: any[]): boolean {
    return buttonPermissions.some(button => button.name === 'insertservices');
  }

  private hasInsertChronicMedicationPermission(buttonPermissions: any[]): boolean {
    return buttonPermissions.some(button => button.name === 'insertchronicmedication');
  }

  getActionButtonsPermission(searchid: string) {
    this.userID = this.authService.getUserID();
    this.sidebarMenu = this.authService.getSidebarMenu();
    const menuItem = this.sidebarMenu.find((item: any) => item.name === searchid);
    if (menuItem) {
      const pageId = menuItem.id;
      this.apiService.create(`api/User/GetPageActionButtons?userid=${this.userID}&pageId=${pageId}`).subscribe({
        next: (response: any) => {
          if (response.isRequestSuccessful === true) {
            const buttonPermissions = response?.successResponse || [];
            this.showFindLimitsButton = this.hasFindLimitsPermission(buttonPermissions);
            this.showInsertServices = this.hasInsertServicesPermission(buttonPermissions);
            this.showInsertChronicMedication = this.hasInsertChronicMedicationPermission(buttonPermissions);
          } else {
            this.toastService.showError(response?.errorDetail?.errorReason)
          }
        },
        error: (error: any) => {
          this.toastService.showError(error)
        }
      });
    }
  }

  fetchMedicalServices() {
    this.pId = this.authService.getCurrentUserPID();
    this.apiService
      .getAll(`api/Claim/GetProviderMedicalServices?providerID=${this.pId}&assuredId=${this.data?.assuredID}`)
      .subscribe({
        next: (response: any) => {
          let data = this.decryptData(response.data);
          const allServices = data.SuccessResponse;
          if(this.printTemplate === 'modern'){
            this.medicalService = allServices.filter(
              (s: any) => ['0310', '0311', '0313'].includes(s.Accntnum)
            );
          } else{
            this.medicalService = allServices;
          }
          this.showFindLimitsButton = this.medicalService.length > 0;
          this.switchtolimit = this.medicalService.length > 0;
        },
        error: (error: any) => {
          this.toastService.showError(error);
        }
      });
  }

  onOTPDigitInput(event: any, index: number) {
    const value = event.target.value.replace(/\D/g, '');
    this.otpDigits[index] = value;
    if (value && index < 5) {
      const nextInput = event.target.parentElement.children[index + 1];
      if (nextInput) {
        nextInput.focus();
      }
    }
  }

  onOTPKeyDown(event: any, index: number) {
    if (event.key === 'Backspace' && !this.otpDigits[index] && index > 0) {
      const prevInput = event.target.parentElement.children[index - 1];
      if (prevInput) {
        prevInput.focus();
      }
    }
    if (event.key === 'ArrowLeft' && index > 0) {
      const prevInput = event.target.parentElement.children[index - 1];
      if (prevInput) {
        prevInput.focus();
      }
    }
    if (event.key === 'ArrowRight' && index < 5) {
      const nextInput = event.target.parentElement.children[index + 1];
      if (nextInput) {
        nextInput.focus();
      }
    }
  }

  onOTPPaste(event: any) {
    event.preventDefault();
    const pastedData = event.clipboardData.getData('text').replace(/\D/g, '');

    if (pastedData.length === 6) {
      for (let i = 0; i < 6; i++) {
        this.otpDigits[i] = pastedData[i];
      }
      const lastInput = event.target.parentElement.children[5];
      if (lastInput) {
        lastInput.focus();
      }
    }
  }

  isOTPComplete(): boolean {
    return this.otpDigits.every(digit => digit !== '');
  }

  getOTPCode(): string {
    return this.otpDigits.join('');
  }

  verifyOTP() {
    if (this.isOTPComplete()) {
      this.loading = true;
      const otpCode = this.getOTPCode();
      const data = {
        assuredID: this.data.assuredID,
        phoneNumber: this.completePhoneNumber,
        otpCode: otpCode,
        otpId: this.otpId
      };

      const endpoint = 'api/OTP/VerifyOTP';

      this.apiService.create(endpoint, data).subscribe({
        next: (response: any) => {
          this.loading = false;
          if (response.isRequestSuccessful === true) {
            this.remainingAttempts = response.successResponse?.remainingAttempts;
            this.translateService.get('toast.otpverifiedsuccessfully').subscribe(text => {
              this.toastService.showSuccess(text || 'OTP verified successfully!');
            });
            this.showOTPSection = false;
            this.showSkipModal = false;
            this.clearOTP();
            this.fetchAssuredData();
          } else {
            this.remainingAttempts = response.successResponse?.remainingAttempts;
            if (this.remainingAttempts >= 0) {
              this.toastService.showError(`${this.remainingAttempts} attempts remaining.`);
            } else {
              const errorMessage = response.errorDetail?.errorReason;
              this.toastService.showError(errorMessage);
            }
          }
        },
        error: (error: any) => {
          this.loading = false;
          const errorMessage = error.errorDetail?.errorReason;
          this.toastService.showError(errorMessage);
        }
      });
    }
  }

  resendOTP() {
    if (this.otpRequestCount >= this.maxOTPRequests) {
      this.translateService.get('toast.maxotprequestsreached').subscribe(text => {
        this.toastService.showWarn(text || 'Maximum OTP requests reached. Please try again later.');
      });
      return;
    }

    if (this.otpTimer > 0) {
      this.translateService.get('toast.otpcooldownactive').subscribe(text => {
        this.toastService.showWarn(text || `Please wait ${this.otpTimer} seconds before requesting another OTP.`);
      });
      return;
    }

    this.sendOTP();
  }



  onPhoneNumberInput(event: any) {
    this.phoneNumber = event.target.value.replace(/\D/g, '');
    // Remove "218" prefix if present at the start
    if (this.phoneNumber.startsWith('218')) {
      this.phoneNumber = this.phoneNumber.substring(3);
    }
    event.target.value = this.phoneNumber;
  }

  startOTPTimer(cooldownSeconds: number) {
    this.otpTimer = cooldownSeconds;
    this.otpTimerInterval = setInterval(() => {
      this.otpTimer--;
      if (this.otpTimer <= 0) {
        clearInterval(this.otpTimerInterval);
      }
    }, 1000);
  }

  formatTimer(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  clearOTP() {
    this.otpDigits = ['', '', '', '', '', ''];
  }

  openSkipModal() {
    this.showSkipModal = true;
  }

  closeSkipModal() {
    this.showSkipModal = false;
    this.resetSkipForm();
  }

  resetSkipForm() {
    this.selectedSkipReason = '';
    this.customReason = '';
  }

  onSkipReasonChange() {
    if (this.selectedSkipReason !== 'other') {
      this.customReason = '';
    }
  }

  submitSkipVerification() {
    if (this.selectedSkipReason === 'other') {
      if (!this.customReason.trim()) {
        this.translateService.get('toast.pleaseprovidecustomreason').subscribe(text => {
          this.toastService.showWarn(text || 'Please provide a custom reason');
        });
        return;
      }
    } else if (!this.selectedSkipReason) {
      this.translateService.get('toast.pleaseselectreason').subscribe(text => {
        this.toastService.showWarn(text || 'Please select a reason');
      });
      return;
    }

    let skipReason = '';

    if (this.selectedSkipReason === 'other') {
      skipReason = this.customReason.trim();
    } else {
      skipReason = this.selectedSkipReason;
    }

    const data = {
      assuredID: this.data.assuredID,
      skipReason: skipReason
    };

    const endpoint = 'api/OTP/SkipVerification';

    this.apiService.create(endpoint, data).subscribe({
      next: (response: any) => {
        if (response.isRequestSuccessful === true) {
          this.translateService.get('toast.skipverificationsuccess').subscribe(text => {
            this.toastService.showSuccess(text || 'Verification skipped successfully');
          });
          this.authService.addSkippedVerification(this.currentAssuredKey);
          this.verificationSkipped = true;
          this.showOTPSection = false;
          this.otpDigits = ['', '', '', '', '', ''];
          this.remainingAttempts = null;
          if (this.otpTimerInterval) {
            clearInterval(this.otpTimerInterval);
          }
          this.otpTimer = 0;
          this.otpRequestCount = 0;
          this.closeSkipModal();
          this.fetchAssuredData();
        } else {
          this.toastService.showError(response.errorDetail.errorReason);
        }
      },
      error: (error: any) => {
        this.toastService.showError(error.errorDetail.errorReason);
      }
    });
  }

  ngOnDestroy() {
    if (this.otpTimerInterval) {
      clearInterval(this.otpTimerInterval);
    }
  }

}



